// FrenchPrep-style Demos tab: rich creation form (name, admin, email, password,
// duration pills, email-creds toggle) + demo-account list with status buckets,
// expandable credentials, +7/+14/+30/+60d extend, and Revoke access now.
import React, { useCallback, useEffect, useState } from 'react';
import { Building2, User, Mail, Lock, Calendar, Plus, Eye, EyeOff, Copy, Check, ChevronDown, ChevronRight, Clock, RotateCcw, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const DURATION_PILLS = [7, 14, 30, 60, 90];
const STATUS_META = {
  active:   { label: 'Active',   badge: 'bg-emerald-100 text-emerald-800' },
  expiring: { label: 'Expiring', badge: 'bg-amber-100 text-amber-800' },
  expired:  { label: 'Expired',  badge: 'bg-rose-100 text-rose-800' },
  revoked:  { label: 'Revoked',  badge: 'bg-slate-200 text-slate-700' },
};

function CopyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <p className="mono-stat">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-slate-100 px-2.5 py-1.5 font-mono text-sm text-slate-800" data-testid={testId}>{value}</code>
        <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* noop */ } }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-700 hover:border-slate-500">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function DemosTab({ onChanged }) {
  const [form, setForm] = useState({ name: '', admin_name: '', admin_email: '', password: '', days: 30, email_credentials: false });
  const [showPw, setShowPw] = useState(false);
  const [result, setResult] = useState(null);
  const [demos, setDemos] = useState([]);
  const [counts, setCounts] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await api.adminDemoAccounts();
      setDemos(res.data.demos); setCounts(res.data.counts || {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load demos.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.admin_email.trim()) return;
    setBusy(true);
    try {
      const res = await api.adminCreateDemoOrgV2({
        name: form.name.trim(), admin_name: form.admin_name.trim(), admin_email: form.admin_email.trim(),
        password: form.password.trim() || undefined, days: Number(form.days) || 30,
        email_credentials: !!form.email_credentials,
      });
      setResult(res.data);
      setForm({ name: '', admin_name: '', admin_email: '', password: '', days: 30, email_credentials: false });
      toast.success('Demo account created.');
      await load(); onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create demo.');
    }
    setBusy(false);
  };

  const extend = async (orgId, days) => {
    try {
      await api.adminExtendAccess(orgId, days);
      toast.success(`Extended +${days}d`);
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed.'); }
  };

  const revoke = async (orgId, name) => {
    if (!window.confirm(`Revoke access for "${name}"?\nThe center admin, instructors, and students will be locked out until you restore.`)) return;
    try {
      await api.adminRevokeAccess(orgId);
      toast.success('Access revoked.');
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed.'); }
  };

  const restore = async (orgId) => {
    try {
      await api.adminRestoreAccess(orgId);
      toast.success('Access restored.');
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed.'); }
  };

  const removeDemo = async (orgId, name) => {
    if (!window.confirm(`Permanently DELETE "${name}"?\nRemoves the org, admin, students, submissions. Cannot be undone.`)) return;
    try {
      await api.adminDeleteOrg(orgId);
      toast.success('Deleted.');
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed.'); }
  };

  const shown = demos.filter((d) => filter === 'all' || d.status === filter);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <section className="mt-6 space-y-6" data-testid="demos-tab">
      {/* Create demo form */}
      <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-5" data-testid="demo-create-form">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-amber-700" />
          <h3 className="font-display text-base font-semibold text-slate-900">Create demo account</h3>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mono-stat flex items-center gap-1"><Building2 className="h-3 w-3" /> Center / organization name</label>
            <Input autoFocus value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Bonjour Academy" className="mt-1 h-10" data-testid="demo-name" />
          </div>
          <div>
            <label className="mono-stat flex items-center gap-1"><User className="h-3 w-3" /> Admin name (the person you&apos;re sending this to)</label>
            <Input value={form.admin_name} onChange={(e) => setForm((f) => ({ ...f, admin_name: e.target.value }))}
              placeholder="e.g. Priya Sharma" className="mt-1 h-10" data-testid="demo-admin-name" />
          </div>
          <div>
            <label className="mono-stat flex items-center gap-1"><Mail className="h-3 w-3" /> Email (this is their username)</label>
            <Input type="email" value={form.admin_email} onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))}
              placeholder="priya@bonjouracademy.in" className="mt-1 h-10" data-testid="demo-admin-email" />
          </div>
          <div>
            <label className="mono-stat flex items-center gap-1"><Lock className="h-3 w-3" /> Password (you&apos;ll share this on WhatsApp)</label>
            <div className="relative mt-1">
              <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Leave blank to auto-generate" className="h-10 pr-9" data-testid="demo-password" />
              <button type="button" onClick={() => setShowPw((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="mono-stat flex items-center gap-1"><Calendar className="h-3 w-3" /> Access duration</label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {DURATION_PILLS.map((d) => (
              <button key={d} type="button" onClick={() => setForm((f) => ({ ...f, days: d }))}
                className={cn('rounded-md border px-3 py-1.5 text-sm font-medium',
                  Number(form.days) === d ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-500 hover:border-slate-300')}
                data-testid={`demo-days-${d}`}>
                {d}d
              </button>
            ))}
            <Input type="number" min={1} max={365} value={form.days}
              onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
              className="h-10 w-20" data-testid="demo-days-custom" />
            <span className="text-xs text-slate-400">days</span>
          </div>
        </div>

        <label className="mt-4 flex items-start gap-2 text-sm text-slate-700" data-testid="demo-email-creds">
          <input type="checkbox" checked={form.email_credentials} onChange={(e) => setForm((f) => ({ ...f, email_credentials: e.target.checked }))} className="mt-0.5" />
          <div>
            <p className="font-medium">Also email the login details to this address</p>
            <p className="text-xs text-slate-500">Optional. Off by default — you can still copy and paste yourself.</p>
          </div>
        </label>

        <div className="mt-4 flex items-center gap-3">
          <Button type="submit" size="lg" className="cta bg-amber-600 hover:bg-amber-700" disabled={busy} data-testid="demo-create-submit">
            <Plus className="h-4 w-4" /> {busy ? 'Creating…' : 'Create demo account'}
          </Button>
        </div>

        {result && (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-white p-4" data-testid="demo-create-result">
            <p className="font-display text-[15px] font-semibold text-slate-900">
              {result.org.name} · demo ready · {result.days} days
            </p>
            <p className="mt-1 text-sm text-slate-500">Sign in at <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{origin}/login</code>.
              {result.email_sent && ' Credentials also emailed.'}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <CopyField label="Email" value={result.admin.email} testId="demo-result-email" />
              <CopyField label="Password" value={result.admin.password} testId="demo-result-password" />
            </div>
          </div>
        )}
      </form>

      {/* Demo accounts list */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-semibold text-slate-900">Demo accounts <span className="ml-2 text-xs font-medium uppercase text-slate-400">{counts.total || 0} total</span></h3>
          <div className="flex flex-wrap items-center gap-1">
            {[
              ['all', 'All', counts.total || 0],
              ['active', 'Active', counts.active || 0],
              ['expiring', 'Expiring', counts.expiring || 0],
              ['expired', 'Expired', counts.expired || 0],
              ['revoked', 'Revoked', counts.revoked || 0],
            ].map(([k, l, n]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={cn('rounded-full px-2.5 py-1 text-xs font-medium',
                  filter === k ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800')}
                data-testid={`demo-filter-${k}`}>
                {l} <span className="ml-1 opacity-70">{n}</span>
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500">No demo accounts in this filter.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {shown.map(({ org, admin, status, days_left, logins }) => {
              const isOpen = expanded === org.org_id;
              const meta = STATUS_META[status] || STATUS_META.active;
              return (
                <div key={org.org_id} className="overflow-hidden rounded-lg border border-slate-200 bg-white"
                  data-testid={`demo-row-${org.org_id}`}>
                  <button type="button" onClick={() => setExpanded(isOpen ? null : org.org_id)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider', meta.badge)}>
                          {meta.label}{typeof days_left === 'number' && status !== 'revoked' && status !== 'expired' && <> · {Math.max(days_left, 0)}d left</>}
                        </span>
                        {logins > 0 && <span className="text-xs text-slate-500">{logins} login{logins === 1 ? '' : 's'}</span>}
                      </div>
                      <p className="mt-0.5 font-display text-[15px] font-semibold text-slate-900">{org.name}</p>
                      <p className="text-xs text-slate-500">{admin?.email || '—'}</p>
                    </div>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4 text-sm" data-testid={`demo-detail-${org.org_id}`}>
                      <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <p className="mono-stat">Credentials (share via WhatsApp)</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                          <CopyField label="Email" value={admin?.email || '—'} />
                          <CopyField label="Password" value="•••••••• (issued at create time)" />
                        </div>
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <div><p className="mono-stat">Admin name</p><p className="mt-0.5 text-slate-800">{admin?.name || '—'}</p></div>
                          <div><p className="mono-stat">Created</p><p className="mt-0.5 text-slate-800">{new Date(org.created_at).toLocaleDateString()}</p></div>
                          <div><p className="mono-stat">Expires</p><p className="mt-0.5 text-slate-800">{org.trial_expires_at ? new Date(org.trial_expires_at).toLocaleDateString() : '—'}</p></div>
                          <div><p className="mono-stat">Last login</p><p className="mt-0.5 text-slate-800">{admin?.last_login ? new Date(admin.last_login).toLocaleString() : 'Never'}</p></div>
                        </div>
                      </div>

                      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4">
                        <p className="mono-stat">Extend Access</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {[7, 14, 30, 60].map((d) => (
                            <button key={d} onClick={() => extend(org.org_id, d)}
                              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                              data-testid={`demo-extend-${d}`}>
                              <Clock className="mr-1 inline h-3 w-3" /> +{d}d
                            </button>
                          ))}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                          {status === 'revoked' ? (
                            <button onClick={() => restore(org.org_id)}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                              data-testid={`demo-restore-${org.org_id}`}>
                              <RotateCcw className="h-3.5 w-3.5" /> Restore access
                            </button>
                          ) : (
                            <button onClick={() => revoke(org.org_id, org.name)}
                              className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50"
                              data-testid={`demo-revoke-${org.org_id}`}>
                              <XCircle className="h-3.5 w-3.5" /> Revoke access now
                            </button>
                          )}
                          <button onClick={() => removeDemo(org.org_id, org.name)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-rose-300 hover:text-rose-700"
                            data-testid={`demo-delete-${org.org_id}`}>
                            Delete permanently
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
